import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FloatButton, Tooltip } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import './TallyLoader.css';
import dayjs from "dayjs";

declare global {
    interface Window {
        Tally?: {
            openPopup: (
                formId: string,
                opts: { layout?: string; width?: number; hiddenFields?: Record<string, string> },
            ) => void;
        };
    }
}

const TALLY_SCRIPT_SRC = 'https://tally.so/widgets/embed.js';
const DEFAULT_FORM_ID = 'w5RPbo';

export const useTallyLoader = (formId: string = DEFAULT_FORM_ID) => {
    const [isScriptReady, setIsScriptReady] = useState(false);
    const scriptAddedByMeRef = useRef(false);

    useEffect(() => {
        const existing = document.querySelector<HTMLScriptElement>(
            `script[src="${TALLY_SCRIPT_SRC}"]`,
        );
        if (existing) {
            // скрипт уже є; якщо Tally вже в window — готові, якщо ні — спробуємо дочекатися load
            if (window.Tally) setIsScriptReady(true);
            else existing.addEventListener('load', () => setIsScriptReady(true), { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = TALLY_SCRIPT_SRC;
        script.async = true;
        scriptAddedByMeRef.current = true;

        script.onload = () => setIsScriptReady(true);
        script.onerror = () => setIsScriptReady(false);

        document.body.appendChild(script);

        return () => {
            // За бажанням: краще не видаляти, щоб не ламати інші інстанси.
            // Якщо дуже треба — видаляйте лише якщо "addedByMe" і ви впевнені що інші не використовують.
            if (scriptAddedByMeRef.current) {
                // script.remove();
            }
        };
    }, []);

    const openTallyWithPrefill = useCallback(
        (fields: Record<string, string>) => {
            if (window.Tally) {
                window.Tally.openPopup(formId, {
                    layout: 'modal',
                    width: 700,
                    hiddenFields: fields,
                });
                return;
            }

            const email = encodeURIComponent(fields?.email ?? '');
            const fullName = encodeURIComponent(fields?.full_name ?? '');

            window
                .open(`https://tally.so/r/${formId}?email=${email}&full_name=${fullName}`, '_blank')
                ?.focus();
        },
        [formId],
    );

    const onOpenTally = useCallback(
        () => {
            if (!isScriptReady) return;

            openTallyWithPrefill({});
        },
        [isScriptReady, openTallyWithPrefill],
    );

    const renderTallyButton = useCallback(
        () => {
            if (!isScriptReady) return null;

            return (
                <Tooltip
                    title={"Відкрити форму зворотного зв'язку"}
                    placement="left"
                    key={`tally_${dayjs().format('YYYY-MM-DD-HH-mm-ss-SSS')}`}>
                    <FloatButton
                        shape="circle"
                        className={"tally-button"}
                        onClick={() => onOpenTally()}
                        icon={<MessageOutlined/>}
                    />
                </Tooltip>
            );
        },
        [isScriptReady, onOpenTally],
    );

    return {
        openTally: openTallyWithPrefill,
        renderTallyButton,
        isScriptReady,
    };
};
